import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import { Calendar } from "../components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { format } from "date-fns";
import {
  Leaf, ArrowLeft, Plus, Bell, Calendar as CalendarIcon,
  Trash2, Clock, AlertCircle
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const RemindersPage = () => {
  const { planId } = useParams();
  const [plan, setPlan] = useState(null);
  const [reminders, setReminders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [newReminder, setNewReminder] = useState({
    plant_name: "",
    task: "",
    frequency: "weekly",
    next_due: new Date(),
    notes: ""
  });

  useEffect(() => {
    fetchData();
  }, [planId]);

  const fetchData = async () => {
    try {
      const [planRes, remindersRes] = await Promise.all([
        axios.get(`${API}/garden-plans/${planId}`),
        axios.get(`${API}/reminders?garden_plan_id=${planId}`)
      ]);
      setPlan(planRes.data);
      setReminders(remindersRes.data);
    } catch (error) {
      console.error("Failed to fetch data:", error);
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const handleCreateReminder = async () => {
    if (!newReminder.plant_name || !newReminder.task) {
      toast.error("Please fill in all required fields");
      return;
    }

    try {
      const response = await axios.post(`${API}/reminders`, {
        ...newReminder,
        garden_plan_id: planId,
        next_due: newReminder.next_due.toISOString()
      });
      setReminders([...reminders, response.data]);
      setDialogOpen(false);
      setNewReminder({
        plant_name: "",
        task: "",
        frequency: "weekly",
        next_due: new Date(),
        notes: ""
      });
      toast.success("Reminder created!");
    } catch (error) {
      console.error("Failed to create reminder:", error);
      toast.error("Failed to create reminder");
    }
  };

  const handleToggleComplete = async (reminderId, currentStatus) => {
    try {
      await axios.patch(`${API}/reminders/${reminderId}?completed=${!currentStatus}`);
      setReminders(reminders.map(r => 
        r.id === reminderId ? { ...r, completed: !currentStatus } : r
      ));
    } catch (error) {
      console.error("Failed to update reminder:", error);
      toast.error("Failed to update reminder");
    }
  };

  const handleDeleteReminder = async (reminderId) => {
    try {
      await axios.delete(`${API}/reminders/${reminderId}`);
      setReminders(reminders.filter(r => r.id !== reminderId));
      toast.success("Reminder deleted");
    } catch (error) {
      console.error("Failed to delete reminder:", error);
      toast.error("Failed to delete reminder");
    }
  };

  const isOverdue = (date) => {
    return new Date(date) < new Date();
  };

  const isDueSoon = (date) => {
    const dueDate = new Date(date);
    const now = new Date();
    const diffDays = (dueDate - now) / (1000 * 60 * 60 * 24);
    return diffDays <= 3 && diffDays >= 0;
  };

  const sortedReminders = [...reminders].sort((a, b) => {
    if (a.completed && !b.completed) return 1;
    if (!a.completed && b.completed) return -1;
    return new Date(a.next_due) - new Date(b.next_due);
  });

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-40 glass border-b border-border/50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link 
              to={`/dashboard/${planId}`}
              className="text-muted-foreground hover:text-primary transition-colors"
              data-testid="back-to-dashboard"
            >
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <div>
              <h1 className="font-serif text-xl font-bold text-foreground">
                Care Reminders
              </h1>
              <p className="text-sm text-muted-foreground">{plan?.name}</p>
            </div>
          </div>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button className="btn-primary" data-testid="add-reminder-btn">
                <Plus className="w-4 h-4 mr-2" />
                Add Reminder
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle className="font-serif text-xl">Create Reminder</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 mt-4">
                <div>
                  <Label htmlFor="plant">Plant</Label>
                  <Select
                    value={newReminder.plant_name}
                    onValueChange={(v) => setNewReminder({ ...newReminder, plant_name: v })}
                  >
                    <SelectTrigger className="mt-1" data-testid="plant-select">
                      <SelectValue placeholder="Select a plant" />
                    </SelectTrigger>
                    <SelectContent>
                      {(plan?.plant_recommendations || []).map((plant, i) => (
                        <SelectItem key={i} value={plant.name}>
                          {plant.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="task">Task</Label>
                  <Input
                    id="task"
                    placeholder="e.g., Water, Prune, Harvest"
                    value={newReminder.task}
                    onChange={(e) => setNewReminder({ ...newReminder, task: e.target.value })}
                    className="mt-1"
                    data-testid="task-input"
                  />
                </div>
                <div>
                  <Label htmlFor="frequency">Frequency</Label>
                  <Select
                    value={newReminder.frequency}
                    onValueChange={(v) => setNewReminder({ ...newReminder, frequency: v })}
                  >
                    <SelectTrigger className="mt-1" data-testid="frequency-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="daily">Daily</SelectItem>
                      <SelectItem value="weekly">Weekly</SelectItem>
                      <SelectItem value="monthly">Monthly</SelectItem>
                      <SelectItem value="seasonal">Seasonal</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Next Due Date</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className="w-full mt-1 justify-start text-left font-normal"
                        data-testid="date-picker-btn"
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {format(newReminder.next_due, "PPP")}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0">
                      <Calendar
                        mode="single"
                        selected={newReminder.next_due}
                        onSelect={(date) => date && setNewReminder({ ...newReminder, next_due: date })}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>
                <div>
                  <Label htmlFor="notes">Notes (optional)</Label>
                  <Input
                    id="notes"
                    placeholder="Additional notes..."
                    value={newReminder.notes}
                    onChange={(e) => setNewReminder({ ...newReminder, notes: e.target.value })}
                    className="mt-1"
                    data-testid="notes-input"
                  />
                </div>
                <Button 
                  onClick={handleCreateReminder} 
                  className="w-full btn-primary"
                  data-testid="save-reminder-btn"
                >
                  Create Reminder
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-8">
        {sortedReminders.length === 0 ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center py-20"
          >
            <div className="w-20 h-20 rounded-full bg-muted flex items-center justify-center mx-auto mb-6">
              <Bell className="w-10 h-10 text-muted-foreground" />
            </div>
            <h2 className="font-serif text-2xl font-semibold mb-2">No reminders yet</h2>
            <p className="text-muted-foreground mb-6">
              Create reminders to stay on top of your garden care
            </p>
          </motion.div>
        ) : (
          <div className="space-y-4">
            {sortedReminders.map((reminder, index) => (
              <motion.div
                key={reminder.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
              >
                <Card 
                  className={`card-organic reminder-card ${
                    reminder.completed ? 'completed' : 
                    isOverdue(reminder.next_due) ? 'overdue' : 
                    isDueSoon(reminder.next_due) ? 'due-soon' : ''
                  }`}
                  data-testid={`reminder-card-${index}`}
                >
                  <CardContent className="p-5">
                    <div className="flex items-start gap-4">
                      <Checkbox
                        checked={reminder.completed}
                        onCheckedChange={() => handleToggleComplete(reminder.id, reminder.completed)}
                        className="mt-1"
                        data-testid={`reminder-checkbox-${index}`}
                      />
                      <div className="flex-1">
                        <div className="flex items-start justify-between">
                          <div>
                            <h3 className={`font-semibold ${reminder.completed ? 'line-through text-muted-foreground' : ''}`}>
                              {reminder.task}
                            </h3>
                            <p className="text-sm text-muted-foreground">
                              {reminder.plant_name}
                            </p>
                          </div>
                          <div className="flex items-center gap-2">
                            {isOverdue(reminder.next_due) && !reminder.completed && (
                              <AlertCircle className="w-4 h-4 text-destructive" />
                            )}
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteReminder(reminder.id)}
                              className="text-muted-foreground hover:text-destructive"
                              data-testid={`delete-reminder-${index}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                        <div className="flex items-center gap-4 mt-3 text-sm text-muted-foreground">
                          <span className="flex items-center gap-1">
                            <CalendarIcon className="w-3 h-3" />
                            {format(new Date(reminder.next_due), "MMM d, yyyy")}
                          </span>
                          <span className="flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {reminder.frequency}
                          </span>
                        </div>
                        {reminder.notes && (
                          <p className="text-sm text-muted-foreground mt-2 italic">
                            {reminder.notes}
                          </p>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default RemindersPage;
